import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, X } from "lucide-react";
import Marquee from "../components/Marquee";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { MaskLine, Reveal } from "../components/Reveal";
import { SERVICES, IMAGES, FLOW_STEPS } from "../data/site";
import { api } from "../lib/api";
import { useSiteImages } from "../lib/useSiteImages";

function Hero() {
  const ref = useRef(null);
  const siteImages = useSiteImages();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex h-[100svh] min-h-[640px] items-start overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={siteImages.hero || IMAGES.hero} alt="建設現場" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
      <div className="absolute inset-0 bg-blueprint opacity-40" />
      <div className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 lg:block">
        <p className="font-mono text-[10px] tracking-[0.5em] text-white/50 [writing-mode:vertical-rl]">
          NEO CONSTRUCTION — TSU, MIE, JAPAN
        </p>
      </div>
      <div className="relative mx-auto w-full max-w-7xl px-4 pt-[118px] sm:px-6 md:pt-[134px] lg:px-8">
        <MaskLine delay={0.15} className="translate-y-7 font-mono text-[11px] tracking-[0.4em] text-accent">
          NEO CONSTRUCTION CO., LTD.
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.88] tracking-tight text-white">
          <MaskLine delay={0.3} className="text-[16vw] sm:text-8xl lg:text-9xl">BUILD</MaskLine>
          <MaskLine delay={0.42} className="text-[16vw] sm:text-8xl lg:text-9xl">
            THE <span className="text-accent">NEXT.</span>
          </MaskLine>
        </h1>
        <MaskLine delay={0.6} className="mt-8 font-mincho text-xl font-bold text-gray-200 sm:text-3xl">
          土地を整え、未来をつくる。
        </MaskLine>
        <Reveal delay={0.75}>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-gray-400 sm:text-base">
            株式会社ネオ建設は、三重県津市を拠点に、土木工事、環境整備、太陽光発電設備工事、不動産管理・開発事業を展開しています。
          </p>
        </Reveal>
        <Reveal delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/service"
            data-testid="hero-service-cta"
            className="group inline-flex items-center gap-3 bg-accent px-8 py-4 font-display text-sm font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
          >
            事業内容を見る
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-cta"
            className="inline-flex items-center gap-3 border border-white/30 px-8 py-4 font-display text-sm font-bold tracking-[0.15em] text-white transition-colors hover:border-accent hover:text-accent"
          >
            お問い合わせ
          </Link>
        </Reveal>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-5 w-5 text-accent" />
        </motion.div>
      </motion.div>
    </section>
  );
}

function BrandFlow({ content }) {
  const [activeStep, setActiveStep] = useState(null);
  const steps = content.flow_steps.map((s, i) => ({ ...s, num: String(i + 1).padStart(2, "0") }));

  return (
    <section className="relative py-[62px] md:py-[82px]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="BRAND MESSAGE"
          title={
            <>
              {content.brand_title_1}
              <br />
              {content.brand_title_2}
            </>
          }
          sub={content.brand_sub}
        />
        <div className="mt-16 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.08} className="bg-ink">
              <button
                type="button"
                data-testid={`flow-step-${step.num}`}
                onClick={() => setActiveStep(step)}
                className="group h-full w-full cursor-pointer p-8 text-left transition-colors hover:bg-coal"
              >
                <p className="font-display text-4xl font-extrabold text-accent">{step.num}</p>
                <h3 className="mt-6 font-mincho text-xl font-bold text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{step.desc}</p>
                <p className="mt-5 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-accent opacity-0 transition-opacity group-hover:opacity-100">
                  詳しく見る
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </p>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {activeStep && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
            onClick={() => setActiveStep(null)}
          >
            <motion.div
              data-testid="flow-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="clip-corner relative w-full max-w-lg border border-line bg-coal p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                data-testid="flow-modal-close"
                onClick={() => setActiveStep(null)}
                className="absolute right-5 top-5 text-gray-500 transition-colors hover:text-accent"
                aria-label="閉じる"
              >
                <X className="h-6 w-6" />
              </button>
              <p className="font-display text-6xl font-extrabold text-accent">{activeStep.num}</p>
              <h3 className="mt-4 font-mincho text-2xl font-bold text-white">{activeStep.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-gray-400">{activeStep.desc}</p>
              <p className="mt-6 border-t border-line pt-6 text-sm leading-loose text-gray-300">{activeStep.detail}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="relative border-t border-line bg-coal py-[67px] md:py-[90px]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="SERVICE — 01/05" title="5つの事業で、土地と向き合う。" />
          <Reveal delay={0.1}>
            <Link
              to="/service"
              data-testid="services-more-link"
              className="group inline-flex items-center gap-2 border border-line px-6 py-3 font-display text-sm font-bold tracking-[0.15em] text-white transition-colors hover:border-accent hover:text-accent"
            >
              事業詳細へ
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link
                to={`/service#${s.slug}`}
                data-testid={`service-row-${s.slug}`}
                className="group grid grid-cols-12 items-center gap-4 border-t border-line py-8 transition-colors last:border-b hover:bg-ink/60 md:gap-8 md:py-10"
              >
                <span className="col-span-2 font-display text-3xl font-extrabold text-accent sm:text-5xl md:col-span-1">{s.num}</span>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-mincho text-2xl font-bold text-white transition-colors group-hover:text-accent sm:text-3xl">{s.titleJa}</h3>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">{s.titleEn}</p>
                </div>
                <p className="col-span-10 col-start-3 text-sm leading-relaxed text-gray-400 md:col-span-4 md:col-start-auto">{s.desc}</p>
                <div className="hidden md:col-span-2 md:block">
                  <div className="clip-corner overflow-hidden border border-line">
                    <img
                      src={s.image}
                      alt={s.titleJa}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorksPreview() {
  const [works, setWorks] = useState([]);
  useEffect(() => {
    api.get("/works").then((r) => setWorks(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="py-[67px] md:py-[90px]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="WORKS" title="施工実績" sub="造成から開発まで。三重県を中心に積み重ねた確かな実績をご覧ください。" />
          <Reveal delay={0.1}>
            <Link
              to="/works"
              data-testid="works-more-link"
              className="group inline-flex items-center gap-2 border border-line px-6 py-3 font-display text-sm font-bold tracking-[0.15em] text-white transition-colors hover:border-accent hover:text-accent"
            >
              実績一覧へ
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {works.map((w, i) => (
            <Reveal key={w.id} delay={i * 0.08}>
              <Link to={`/works/${w.id}`} data-testid={`works-preview-card-${i}`} className="clip-corner group relative block overflow-hidden border border-line">
                <img src={w.image_url || IMAGES.civil} alt={w.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                <div className="absolute bottom-0 w-full p-6">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-accent">
                    {w.category} — {w.year}
                  </p>
                  <h3 className="mt-2 font-mincho text-lg font-bold text-white">{w.title}</h3>
                  <p className="mt-1 text-xs text-gray-400">{w.location}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsStrip() {
  const stats = [
    { value: "三重県津市", label: "本社拠点 / HEAD OFFICE" },
    { value: "5", label: "事業領域 / BUSINESS DOMAINS" },
    { value: "東海圏", label: "対応地域 / SERVICE AREA" },
    { value: "一貫対応", label: "造成から不動産開発まで" },
  ];
  return (
    <section data-testid="stats-strip" className="border-y border-line bg-black">
      <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <div key={i} className="border-line p-8 odd:border-r lg:border-r lg:last:border-r-0">
            <p className="font-mincho text-2xl font-bold text-white sm:text-3xl">{s.value}</p>
            <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const DEFAULT_BRAND = {
  brand_title_1: "建設から不動産まで、",
  brand_title_2: "土地の価値をつくる。",
  brand_sub: "ネオ建設は単なる建設会社ではありません。土地を整え、建設し、維持管理し、不動産として活用・開発するまで。土地のライフサイクル全体に一貫して関われる会社です。",
  flow_steps: FLOW_STEPS,
};

export default function Home() {
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  useEffect(() => {
    api
      .get("/home")
      .then((r) => {
        if (r.data && Array.isArray(r.data.flow_steps) && r.data.flow_steps.length) setBrand(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <BrandFlow content={brand} />
      <ServicesPreview />
      <WorksPreview />
      <StatsStrip />
      <CtaSection />
    </div>
  );
}
